import { isoDate as melDate } from "@/lib/intl";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CreditCard, Search, Sparkles, Users, Zap, CheckCircle2, XCircle, SkipForward } from "lucide-react";
import { toast } from "sonner";

function fmtAud(cents) {
  return `A$${(cents / 100).toFixed(2)}`;
}

function fmtDateEpoch(s) {
  if (!s) return "";
  const d = new Date(s * 1000);
  return d.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
}

export default function MembershipRenewalForm({
  itemCodes = [],
  onDraft,
  initialEmail = "",
  initialChargeId = "",
  initialCertCode = "",
  initialBaseDesc = "",
}) {
  const [email, setEmail] = useState(initialEmail);
  const [stripeBusy, setStripeBusy] = useState(false);
  const [stripeData, setStripeData] = useState(null); // {customers, charges}
  const [chargeId, setChargeId] = useState(""); // selected Stripe charge.id
  const [certCode, setCertCode] = useState(initialCertCode);
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [itemCode, setItemCode] = useState("502");
  // No hardcoded description default — the wrong "Exercise Physiology
  // Small Group Sessions Twenty Credits" text was silently poisoning
  // membership receipts for other product lines (e.g. Bernard Toulet's
  // GLA:D Graduates Membership). Description is now filled from one
  // of three sources, in priority order:
  //   1. initialBaseDesc — passed by the amber banner hand-off with
  //      the product name from the order (e.g. "GLA:D Graduates -
  //      Membership 2 class/wk").
  //   2. subscription_mappings — auto-fetched on email change, so
  //      the next receipt for a known customer keeps their previous
  //      description.
  //   3. Manual entry — placeholder guides the user.
  const [baseDesc, setBaseDesc] = useState(initialBaseDesc || "");
  // Small badge under the description field flagging whether the
  // value was auto-populated (so the reviewer knows to double-check
  // before hitting Build).
  const [descSource, setDescSource] = useState(
    initialBaseDesc ? "banner" : null,
  );
  const [previewBusy, setPreviewBusy] = useState(false);
  const [preview, setPreview] = useState(null); // {clients:[{key,name,email,dates}]}
  const [includeKeys, setIncludeKeys] = useState({}); // key -> bool
  const [draftBusy, setDraftBusy] = useState(false);
  const [existing, setExisting] = useState(null); // existing receipt for this Stripe charge
  const [regenBusy, setRegenBusy] = useState(false);

  // Auto-load saved cert code + description + item code from the
  // subscription_mappings collection whenever the email changes.
  // Only fills empty fields so any manual edits or banner-provided
  // values win. Silent on 404 (no mapping yet) or network errors.
  useEffect(() => {
    const e = (email || "").trim().toLowerCase();
    if (!e) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/receipts/membership/mapping", {
          params: { email: e },
        });
        if (cancelled) return;
        const m = r.data || {};
        if (m.certificate_code && !certCode) setCertCode(m.certificate_code);
        if (m.item_code && itemCode === "502") setItemCode(m.item_code);
        if (m.base_description && !baseDesc) {
          setBaseDesc(m.base_description);
          setDescSource("mapping");
        }
      } catch {
        /* no saved mapping — expected for new customers */
      }
    })();
    return () => { cancelled = true; };
    // Intentionally only re-run on email change; refetching on every
    // baseDesc/certCode keystroke would clobber user edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  // Bulk "Generate all renewals" state — mirrors the single-charge
  // controls but persists one receipt per unbilled Stripe charge in
  // a single round-trip. Result is a summary table shown in-form.
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkSince, setBulkSince] = useState("");
  const [bulkResult, setBulkResult] = useState(null);

  async function runBulkGenerate() {
    if (!certCode.trim()) {
      toast.error("Enter the Acuity certificate code first");
      return;
    }
    if (!email.trim()) {
      toast.error("Enter a customer email first");
      return;
    }
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const r = await api.post(
        "/receipts/membership/bulk-generate",
        {
          email: email.trim(),
          certificate_code: certCode.trim(),
          item_code: itemCode || "",
          base_description: baseDesc || "Membership renewal",
          since_date_iso: bulkSince || null,
        },
        { timeout: 180000 },
      );
      setBulkResult(r.data);
      const s = r.data.summary || {};
      toast.success(
        `Created ${s.created} · skipped ${s.skipped} · failed ${s.failed}`,
        {
          description:
            s.failed > 0
              ? "Check the summary table below for failed rows"
              : "Every unbilled renewal has a receipt. Download from the log.",
          duration: 8000,
        },
      );
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error("Bulk generate failed", { description: detail });
    } finally {
      setBulkBusy(false);
    }
  }

  // Whenever the selected Stripe charge changes, check if a receipt was
  // already generated for it (lets the user re-download instead of rebuilding).
  useEffect(() => {
    if (!chargeId) { setExisting(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/receipts/by-source", {
          params: { source: "membership", source_id: chargeId },
          timeout: 15000,
        });
        if (!cancelled) setExisting(r.data);
      } catch (e) {
        if (!cancelled) setExisting(null);
      }
    })();
    return () => { cancelled = true; };
  }, [chargeId]);

  async function regenExisting(fmt) {
    if (!existing?.sale_id) return;
    setRegenBusy(true);
    try {
      const res = await api.post(
        `/receipts/regenerate/${encodeURIComponent(existing.sale_id)}`,
        null,
        { params: { format: fmt }, responseType: "blob", timeout: 90000 },
      );
      const blob = new Blob([res.data], {
        type: fmt === "pdf"
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${existing.sale_id}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Re-downloaded ${existing.sale_id} as ${fmt.toUpperCase()}`);
    } catch (e) {
      let detail = e?.message;
      if (e?.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          detail = JSON.parse(text)?.detail || text;
        } catch { /* ignore */ }
      } else {
        detail = e?.response?.data?.detail || e?.message;
      }
      toast.error("Re-download failed", { description: detail });
    } finally {
      setRegenBusy(false);
    }
  }

  // Auto-trigger Stripe load + charge selection if launched from a URL with prefill.
  useEffect(() => {
    if (!initialEmail) return;
    let cancelled = false;
    (async () => {
      setStripeBusy(true);
      try {
        const [stripeR, mappingR] = await Promise.allSettled([
          api.get("/stripe/payments/by-email", {
            params: { email: initialEmail.trim() },
            timeout: 60000,
          }),
          api.get("/receipts/membership/mapping", {
            params: { email: initialEmail.trim() },
            timeout: 15000,
          }),
        ]);
        if (cancelled) return;
        if (stripeR.status === "rejected") throw stripeR.reason;
        const r = stripeR.value;
        setStripeData(r.data);
        const succeeded = (r.data.charges || []).filter((c) => c.status === "succeeded");
        const pick =
          (initialChargeId && succeeded.find((c) => c.id === initialChargeId)?.id) ||
          succeeded[0]?.id ||
          "";
        if (pick) {
          setChargeId(pick);
          autoFillPeriodFromCharges(succeeded, pick);
        }
        if (mappingR.status === "fulfilled") {
          const m = mappingR.value.data || {};
          if (m.certificate_code) setCertCode(m.certificate_code);
          if (m.item_code) setItemCode(m.item_code);
          if (m.base_description) setBaseDesc(m.base_description);
          toast.success("Pre-filled from saved mapping — click Preview to continue");
        } else if (pick) {
          toast.success("Pre-filled from Stripe — paste the certificate code to continue");
        }
      } catch (e) {
        const detail = e?.response?.data?.detail || e.message;
        toast.error("Stripe lookup failed", { description: detail });
      } finally {
        if (!cancelled) setStripeBusy(false);
      }
    })();
    return () => { cancelled = true; };
    // Only run once on initial mount with the prefill.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadStripe() {
    if (!email.trim()) {
      toast.error("Enter a customer email");
      return;
    }
    setStripeBusy(true);
    setStripeData(null);
    setChargeId("");
    setPreview(null);
    try {
      // Fire both in parallel: Stripe charges + the saved cert-code mapping
      // (404 on mapping is fine — first time we see this customer).
      const [stripeR, mappingR] = await Promise.allSettled([
        api.get("/stripe/payments/by-email", {
          params: { email: email.trim() },
          timeout: 60000,
        }),
        api.get("/receipts/membership/mapping", {
          params: { email: email.trim() },
          timeout: 15000,
        }),
      ]);
      if (stripeR.status === "rejected") throw stripeR.reason;
      const r = stripeR.value;
      setStripeData(r.data);
      const succeeded = (r.data.charges || []).filter((c) => c.status === "succeeded");
      if (succeeded.length === 0) {
        toast.info("No succeeded Stripe charges for this email");
      } else {
        setChargeId(succeeded[0].id);
        autoFillPeriodFromCharges(succeeded, succeeded[0].id);
      }
      // Apply saved mapping (if any) — but only override fields the user
      // hasn't typed into yet so we don't clobber edits.
      if (mappingR.status === "fulfilled") {
        const m = mappingR.value.data || {};
        if (m.certificate_code && !certCode) setCertCode(m.certificate_code);
        if (m.item_code) setItemCode(m.item_code);
        if (m.base_description) setBaseDesc(m.base_description);
        toast.success("Found saved certificate for this customer", {
          description: `Cert ${m.certificate_code} — ready to preview`,
        });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      if (/not configured/i.test(detail)) {
        toast.error("Stripe is not connected", {
          description: "Settings → Stripe — Payment reconciliation",
        });
      } else {
        toast.error("Stripe lookup failed", { description: detail });
      }
    } finally {
      setStripeBusy(false);
    }
  }

  function autoFillPeriodFromCharges(succeeded, selectedId) {
    const idx = succeeded.findIndex((c) => c.id === selectedId);
    if (idx < 0) return;
    const thisCh = succeeded[idx];
    const prevCh = succeeded[idx - 1]; // newer (lower idx, since sorted desc)
    const start = melDate(new Date(thisCh.created * 1000));
    // Period ends at the NEXT payment (exclusive). If this is the most recent
    // charge, end at today + 1 day.
    let end;
    if (prevCh) {
      end = melDate(new Date(prevCh.created * 1000));
    } else {
      end = melDate(new Date(Date.now() + 86400000));
    }
    setPeriodStart(start);
    setPeriodEnd(end);
  }

  function onSelectCharge(id) {
    setChargeId(id);
    const succeeded = (stripeData?.charges || []).filter((c) => c.status === "succeeded");
    autoFillPeriodFromCharges(succeeded, id);
  }

  async function runPreview() {
    if (!certCode.trim() || !periodStart || !periodEnd) {
      toast.error("Need certificate code + period dates");
      return;
    }
    setPreviewBusy(true);
    try {
      const r = await api.get("/receipts/membership/lookup", {
        params: {
          certificate_code: certCode.trim(),
          period_start: periodStart,
          period_end: periodEnd,
        },
        timeout: 60000,
      });
      setPreview(r.data);
      // Default: include all detected clients
      const next = {};
      (r.data.clients || []).forEach((c) => { next[c.key] = true; });
      setIncludeKeys(next);
      if ((r.data.clients || []).length === 0) {
        toast.info("No appointments found for this certificate in the period");
      } else {
        toast.success(
          `${r.data.appointments_total} appointment${r.data.appointments_total === 1 ? "" : "s"} across ${r.data.clients.length} client${r.data.clients.length === 1 ? "" : "s"}`,
        );
      }
    } catch (e) {
      toast.error("Lookup failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setPreviewBusy(false);
    }
  }

  async function buildDraft() {
    const charge = (stripeData?.charges || []).find((c) => c.id === chargeId);
    if (!charge) {
      toast.error("Select a Stripe payment first");
      return;
    }
    if (!certCode.trim()) {
      toast.error("Enter the Acuity certificate code");
      return;
    }
    const customer = stripeData?.customers?.[0];
    const includeClientKeys = Object.entries(includeKeys)
      .filter(([_, v]) => v)
      .map(([k]) => k);
    setDraftBusy(true);
    try {
      const payload = {
        client_name: customer?.name || email.split("@")[0],
        client_email: email.trim(),
        certificate_code: certCode.trim(),
        payment_date_iso: melDate(new Date(charge.created * 1000)),
        payment_amount: (charge.amount || 0) / 100,
        period_start_iso: periodStart,
        period_end_iso: periodEnd,
        base_description: baseDesc,
        item_code: itemCode,
        include_client_keys: includeClientKeys.length ? includeClientKeys : null,
        payment_method: "Credit Card",
        stripe_charge_id: chargeId,
      };
      const r = await api.post("/receipts/membership/draft", payload, { timeout: 90000 });
      onDraft(r.data);
      toast.success("Draft built — edit below and generate when ready");
    } catch (e) {
      toast.error("Draft build failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setDraftBusy(false);
    }
  }

  const succeeded = (stripeData?.charges || []).filter((c) => c.status === "succeeded");

  return (
    <div className="mt-4 space-y-4 border-t border-zinc-200 pt-4">
      {/* Step 1: email + Stripe lookup */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[260px]">
          <Label className="text-xs">Customer email</Label>
          <Input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") loadStripe(); }}
            placeholder="shane.derham91@gmail.com"
            data-testid="membership-email"
          />
        </div>
        <Button
          onClick={loadStripe}
          disabled={stripeBusy}
          className="bg-zinc-950 hover:bg-zinc-800 text-white"
          data-testid="membership-load-stripe-btn"
        >
          <CreditCard className="h-4 w-4 mr-1.5" />
          {stripeBusy ? "Loading…" : "Load Stripe payments"}
        </Button>
      </div>

      {/* Step 2: select payment */}
      {succeeded.length > 0 && (
        <div>
          <Label className="text-xs">Stripe payment to receipt</Label>
          <Select value={chargeId} onValueChange={onSelectCharge}>
            <SelectTrigger data-testid="membership-charge-select">
              <SelectValue placeholder="Choose a payment…" />
            </SelectTrigger>
            <SelectContent>
              {succeeded.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {fmtDateEpoch(c.created)} — {fmtAud(c.amount)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Bulk "generate all renewals" — persists one receipt per
          unbilled Stripe charge in a single click. Skips anything
          that already has a receipt (source_id match) and honours
          the optional "since" date so the user can bulk-catch-up on
          the last few months without re-doing older receipts. */}
      {succeeded.length > 0 && (
        <div className="rounded-md border border-indigo-300 bg-indigo-50/60 p-3 space-y-2"
             data-testid="bulk-generate-panel">
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-indigo-700" />
            <div className="text-sm font-semibold text-indigo-900">
              Generate ALL renewals in one click
            </div>
          </div>
          <div className="text-xs text-indigo-800">
            Creates one receipt per unbilled Stripe charge for this
            certificate. Already-receipted charges are skipped.
            Requires the certificate code above.
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label className="text-2xs">Since (optional)</Label>
              <Input
                type="date"
                value={bulkSince}
                onChange={(e) => setBulkSince(e.target.value)}
                className="h-8 text-xs w-40"
                data-testid="bulk-since-date"
              />
            </div>
            <Button
              size="sm"
              onClick={runBulkGenerate}
              disabled={bulkBusy || !certCode.trim()}
              className="bg-indigo-700 hover:bg-indigo-800 text-white"
              data-testid="bulk-generate-btn"
            >
              <Zap className="h-3.5 w-3.5 mr-1" />
              {bulkBusy ? "Generating…" : `Generate all (${succeeded.length} charge${succeeded.length === 1 ? "" : "s"})`}
            </Button>
          </div>

          {bulkResult && (
            <div className="mt-2 space-y-2" data-testid="bulk-generate-result">
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-emerald-300 bg-white text-emerald-800">
                  <CheckCircle2 className="h-3 w-3" /> Created {bulkResult.summary.created}
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-zinc-300 bg-white text-zinc-700">
                  <SkipForward className="h-3 w-3" /> Skipped {bulkResult.summary.skipped}
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-red-300 bg-white text-red-700">
                  <XCircle className="h-3 w-3" /> Failed {bulkResult.summary.failed}
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-zinc-300 bg-white text-zinc-700">
                  Total {bulkResult.summary.total}
                </span>
              </div>
              <div className="border border-zinc-200 rounded overflow-hidden bg-white">
                <table className="w-full text-xs">
                  <thead className="bg-zinc-50 text-zinc-600">
                    <tr>
                      <th className="text-left px-2 py-1">Payment date</th>
                      <th className="text-left px-2 py-1">Amount</th>
                      <th className="text-left px-2 py-1">Status</th>
                      <th className="text-left px-2 py-1">Sale ID / Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(bulkResult.results || []).map((r) => (
                      <tr key={r.charge_id} className="border-t border-zinc-100"
                          data-testid={`bulk-row-${r.charge_id}`}>
                        <td className="px-2 py-1 text-zinc-900">{r.payment_date_iso}</td>
                        <td className="px-2 py-1 text-zinc-900">A${r.amount.toFixed(2)}</td>
                        <td className="px-2 py-1">
                          {r.status === "created" && (
                            <span className="text-emerald-700">Created</span>
                          )}
                          {r.status === "skipped_existing" && (
                            <span className="text-zinc-600">Skipped (already receipted)</span>
                          )}
                          {r.status === "failed" && (
                            <span className="text-red-700">Failed</span>
                          )}
                        </td>
                        <td className="px-2 py-1 text-zinc-700 font-mono-num">
                          {r.sale_id || r.error || ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* "Already receipted" banner */}
      {existing && (
        <div className="border border-emerald-300 bg-emerald-50 rounded-md p-3" data-testid="existing-receipt-banner">
          <div className="flex items-start gap-2">
            <Sparkles className="h-4 w-4 mt-0.5 text-emerald-700 flex-shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-emerald-900">
                Receipt already generated for this payment
              </div>
              <div className="text-xs text-emerald-800 mt-0.5 font-mono-num">
                Sale ID: {existing.sale_id} · {existing.generated_at?.slice(0, 10)}
                {existing.emailed_to ? ` · emailed to ${existing.emailed_to}` : ""}
              </div>
              <div className="mt-2 flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 border-emerald-300 bg-white hover:bg-emerald-100 text-emerald-800"
                  onClick={() => regenExisting("pdf")}
                  disabled={regenBusy}
                  data-testid="existing-receipt-pdf-btn"
                >
                  Re-download PDF
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 border-emerald-300 bg-white hover:bg-emerald-100 text-emerald-800"
                  onClick={() => regenExisting("docx")}
                  disabled={regenBusy}
                  data-testid="existing-receipt-docx-btn"
                >
                  Re-download DOCX
                </Button>
              </div>
              <div className="mt-2 text-2xs text-emerald-700">
                You can still build a new draft below to overwrite this receipt if details have changed.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: cert code + period */}
      {chargeId && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Certificate code</Label>
            <Input
              value={certCode}
              onChange={(e) => setCertCode(e.target.value.toUpperCase())}
              placeholder="F38961FE"
              className="font-mono-num"
              data-testid="membership-cert-input"
            />
            <p className="mt-1 text-2xs text-zinc-500">
              From Acuity → client → Packages, gifts & subscriptions.
            </p>
          </div>
          <div>
            <Label className="text-xs">Period start (inclusive)</Label>
            <Input
              type="date"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
              data-testid="membership-period-start"
            />
          </div>
          <div>
            <Label className="text-xs">Period end (exclusive)</Label>
            <Input
              type="date"
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
              data-testid="membership-period-end"
            />
          </div>
          <div className="flex items-end">
            <Button
              onClick={runPreview}
              disabled={previewBusy || !certCode.trim()}
              variant="outline"
              className="w-full border-zinc-300"
              data-testid="membership-preview-btn"
            >
              <Search className={`h-4 w-4 mr-1.5 ${previewBusy ? "animate-pulse" : ""}`} />
              {previewBusy ? "Looking…" : "Preview appointments"}
            </Button>
          </div>
        </div>
      )}

      {/* Step 4: client toggles */}
      {preview?.clients?.length > 0 && (
        <div className="border border-zinc-200 rounded-md bg-zinc-50 p-3">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-zinc-700 mb-2">
            <Users className="h-3.5 w-3.5" />
            Clients who used this certificate in the period
          </div>
          <div className="space-y-1.5">
            {preview.clients.map((c) => (
              <label
                key={c.key}
                className="flex items-start gap-2 text-sm cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={!!includeKeys[c.key]}
                  onChange={(e) =>
                    setIncludeKeys((prev) => ({ ...prev, [c.key]: e.target.checked }))
                  }
                  className="mt-1"
                  data-testid={`membership-include-${c.key}`}
                />
                <div>
                  <div className="font-medium text-zinc-900">{c.name}</div>
                  <div className="text-2xs text-zinc-500 font-mono-num">
                    {c.email || "no email"} · {c.dates.length} session{c.dates.length === 1 ? "" : "s"}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Step 5: description / item code */}
      {chargeId && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <Label className="text-xs">Service description (before dates)</Label>
            <Input
              value={baseDesc}
              onChange={(e) => {
                setBaseDesc(e.target.value);
                setDescSource("manual");
              }}
              placeholder="e.g. GLA:D Graduates Membership — 2 classes per week"
              data-testid="membership-base-desc"
            />
            {descSource === "mapping" && (
              <div className="text-2xs text-zinc-500 mt-1"
                   data-testid="desc-source-badge">
                Auto-filled from this customer&apos;s previous receipt —
                edit if the product is different.
              </div>
            )}
            {descSource === "banner" && (
              <div className="text-2xs text-emerald-700 mt-1"
                   data-testid="desc-source-badge">
                Filled from the order&apos;s product name.
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs">Item code</Label>
            <Select value={itemCode} onValueChange={setItemCode}>
              <SelectTrigger data-testid="membership-item-code">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(itemCodes.length ? itemCodes : [{ code: "502" }]).map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {c.code}
                    {c.label ? ` — ${c.label}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Final: build draft */}
      {chargeId && (
        <div>
          <Button
            onClick={buildDraft}
            disabled={draftBusy}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="membership-build-draft-btn"
          >
            <Sparkles className={`h-4 w-4 mr-1.5 ${draftBusy ? "animate-pulse" : ""}`} />
            {draftBusy ? "Building…" : "Build draft receipt"}
          </Button>
          <p className="mt-2 text-2xs text-zinc-500">
            Builds an editable draft in the form below. Generate the DOCX/PDF as usual.
          </p>
        </div>
      )}
    </div>
  );
}
